// apps/web/src/features/admin/form-config/api/client.ts
import { api, ApiError } from '@/lib/api-client';
import type { JobTypeVersionContent, JobTypeVersionContentV3, TryItBranchResult } from '@capture/shared';

/**
 * F-038 — outcome of the OCR caller-profile sync that runs on publish.
 *   ok      → profile synced to the OCR service
 *   failed  → sync threw (extraction may be stale); `error` carries the message
 *   skipped → job type has no OCR caller-profile (ocrExtractionMode unset)
 */
export interface OcrProfileSyncResult {
  status: 'ok' | 'failed' | 'skipped';
  syncedAt?: string;
  error?: string;
}

export interface JobTypeVersion {
  id: string;
  jobTypeId: string;
  versionNumber: number;
  state: 'draft' | 'published' | 'archived';
  content: JobTypeVersionContent;
  /**
   * F-032 — optimistic-concurrency stamp. Every draft write must send the
   * revision it loaded (`baseRevision`); the server 409s (`stale_draft`) if
   * another surface wrote the draft in between.
   */
  contentRevision: number;
  changelog: Record<string, unknown>;
  publishNote: string | null;
  createdBy: string | null;
  createdAt: string;
  publishedBy: string | null;
  publishedAt: string | null;
  /**
   * Resolved creator (set by the listing endpoint). `null` when `createdBy`
   * is null, doesn't match a user in the tenant, or single-version endpoints
   * that don't resolve the user. UI must fall back to `createdBy` when null.
   */
  user?: { id: string; name: string | null } | null;
  /**
   * R-79 — resolved publisher (set by the listing endpoint), mirror of
   * `user`. `null` when unpublished, unresolved, or on single-version
   * endpoints. UI falls back to `publishedBy` (raw id) when null.
   */
  publishedByUser?: { id: string; name: string | null } | null;
}

// All form-config calls flow through `api` so 401 bounces to Suite SSO
// (the Skarn non-negotiable 401 handling contract).
export const formConfigApi = {
  getCurrentPublished: (jobTypeId: string) =>
    api.get<JobTypeVersion | null>(`/job-types/${jobTypeId}/versions/current`).catch((e) => {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }),

  listVersions: (jobTypeId: string) =>
    api.get<JobTypeVersion[]>(`/job-types/${jobTypeId}/versions`),

  getVersion: (versionId: string) =>
    api.get<JobTypeVersion>(`/job-type-versions/${versionId}`),

  createDraft: (jobTypeId: string) =>
    api.post<JobTypeVersion>(`/job-types/${jobTypeId}/versions/draft`),

  updateDraft: (
    versionId: string,
    content: JobTypeVersionContent | JobTypeVersionContentV3,
    baseRevision: number,
  ) => api.patch<JobTypeVersion>(`/job-type-versions/${versionId}`, { content, baseRevision }),

  /**
   * F-032 — the one place that knows the stale-draft wire shape
   * (409 + `{ error: 'stale_draft', currentRevision }`). Callers branch on
   * this instead of hand-casting `err.body` at every catch block.
   */
  isStaleDraftError: (e: unknown): e is ApiError & { body: { error: 'stale_draft'; currentRevision: number } } =>
    e instanceof ApiError &&
    e.status === 409 &&
    (e.body as { error?: string } | null)?.error === 'stale_draft',

  discardDraft: (jobTypeId: string) =>
    api.delete<void>(`/job-types/${jobTypeId}/versions/draft`),

  publishDraft: (
    jobTypeId: string,
    versionId: string,
    body: { publishNote: string; typedConfirmation: string; overrideReason?: string },
  ) =>
    // F-038 — the publish response now carries the OCR caller-profile sync
    // outcome so the operator learns whether OCR extraction is live, instead of
    // a fire-and-forget sync that failed silently.
    api.post<JobTypeVersion & { ocrProfileSync?: OcrProfileSyncResult }>(
      `/job-types/${jobTypeId}/versions/${versionId}/publish`,
      body,
    ),

  restore: (jobTypeId: string, sourceVersionId: string, opts?: { replace?: boolean }) =>
    api.post<JobTypeVersion>(
      `/job-types/${jobTypeId}/versions/${sourceVersionId}/restore`,
      opts ?? {},
    ),

  cherryPick: (
    jobTypeId: string,
    body: {
      sourceVersionId: string;
      selection: {
        fieldNames?: string[];
        pipeline?: boolean;
        headerMapping?: boolean;
        ruleIds?: string[];
        destinationIds?: string[];
      };
    },
  ) =>
    api.post<JobTypeVersion>(`/job-types/${jobTypeId}/versions/cherry-pick`, body),

  /**
   * Try-it (Task 12): dry-run an UNSAVED automation — the panel compiles it
   * client-side (`compileAutomation`) and posts the resulting rules here.
   * The server evaluates with the live evaluators, pure and read-only —
   * nothing is persisted, so this is safe to call for an in-progress draft
   * edit that hasn't been saved yet.
   */
  automationTestRun: (
    versionId: string,
    body: { fields: Record<string, string>; stage: string; rules: unknown[] },
  ) =>
    api.post<{ branches: TryItBranchResult[] }>(
      `/job-type-versions/${versionId}/automation-test-run`,
      body,
    ),
};
